import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  type KeyboardEvent,
  type ReactElement,
} from "react";
import styles from "./ComposerField.module.css";

const LINE_HEIGHT_PX = 20;
const VERTICAL_PADDING_PX = 20;

export interface ComposerFieldProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Enter without Shift. The caller decides whether the draft is submittable. */
  readonly onSubmit: () => void;
  /** Escape. */
  readonly onCancel: () => void;
  readonly placeholder: string;
  readonly ariaLabel: string;
  readonly autoFocus?: boolean;
  readonly disabled?: boolean;
  /** Growth ceiling in text rows. The field scrolls past it. */
  readonly maxRows?: number;
  readonly testId?: string;
}

/**
 * One-line composer that grows with its text. Enter submits, Shift+Enter
 * inserts a newline, Escape cancels. The soft accent border and halo on
 * focus match the `TextInput` primitive; the field owns them because the
 * primitive is single-line.
 */
export const ComposerField = forwardRef<HTMLTextAreaElement, ComposerFieldProps>(
  function ComposerField(
    {
      value,
      onChange,
      onSubmit,
      onCancel,
      placeholder,
      ariaLabel,
      autoFocus = false,
      disabled = false,
      maxRows = 5,
      testId,
    },
    ref,
  ): ReactElement {
    const innerRef = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);

    useLayoutEffect(() => {
      const el = innerRef.current;
      if (el === null) return;
      el.style.height = "auto";
      const max = maxRows * LINE_HEIGHT_PX + VERTICAL_PADDING_PX;
      const next = Math.min(el.scrollHeight, max);
      el.style.height = `${String(Math.max(next, 40))}px`;
      el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
    }, [value, maxRows]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.nativeEvent.isComposing) return;
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          onSubmit();
          return;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          onCancel();
        }
      },
      [onSubmit, onCancel],
    );

    return (
      <textarea
        ref={innerRef}
        className={styles.field}
        data-focus-style="custom"
        data-testid={testId}
        rows={1}
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoFocus={autoFocus}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={handleKeyDown}
      />
    );
  },
);
